// src/pages/ControlPanel.js — Dark Theme Control Panel
import React, { useEffect, useState, useMemo } from "react";
import { Form, Button, Modal, Tabs, Tab } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import toast from "react-hot-toast";
import { ROLES } from "../utils/constants";
import BrandSettings from "./BrandSettings";
import ActiveSessions from "../components/security/ActiveSessions";
import PolicyComplianceAdmin from "../components/onboarding/PolicyComplianceAdmin";
import PasswordInput from "../components/common/PasswordInput";
import PasswordStrengthMeter from "../components/common/PasswordStrengthMeter";
import { Shield, UserPlus, Key, Lock, Unlock, Trash2 } from "lucide-react";
import "./controlpanel.css";
import "./TeamPortfolio.css";

const ControlPanel = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPassModal, setShowPassModal] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);

  // Password change form states
  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [signOutOthers, setSignOutOthers] = useState(true);
  const [passLoading, setPassLoading] = useState(false);
  const [addEmpLoading, setAddEmpLoading] = useState(false);

  const [newEmp, setNewEmp] = useState({ name: "", email: "", password: "", role: "Agent" });

  const isAdmin = user?.role === "Admin";

  useEffect(() => {
    setLoading(true);
    api.get("/employees").then((res) => setEmployees(Array.isArray(res.data) ? res.data : []))
      .catch(() => toast.error("Failed to load employees"))
      .finally(() => setLoading(false));
  }, []);

  const managers = useMemo(() => employees.filter((e) => e.role === "Manager"), [employees]);

  const handleAdd = async () => {
    if (!newEmp.name || !newEmp.email || !newEmp.password) {
      toast.error("Name, email, and password are required");
      return;
    }
    if (newEmp.password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    setAddEmpLoading(true);
    try {
      const { data } = await api.post("/auth/register", newEmp);
      setEmployees((prev) => [data, ...prev]);
      setShowModal(false);
      setNewEmp({ name: "", email: "", password: "", role: "Agent" });
      toast.success("Employee added successfully!");
      const res = await api.get("/employees");
      setEmployees(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || "Failed to add employee");
    } finally {
      setAddEmpLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPass) {
      toast.error("New password is required");
      return;
    }
    if (newPass.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return;
    }
    if (newPass !== confirmPass) {
      toast.error("New password and confirmation do not match");
      return;
    }

    const isOwnPassword = selectedEmp?.id === user?.id || selectedEmp?.email === user?.email;
    if (isOwnPassword && !currentPass) {
      toast.error("Please enter your current password");
      return;
    }

    setPassLoading(true);
    try {
      await api.put(`/employees/password/${encodeURIComponent(selectedEmp.email)}`, {
        password: newPass,
        currentPassword: currentPass || undefined,
        signOutOthers
      });

      setShowPassModal(false);
      setNewPass("");
      setConfirmPass("");
      setCurrentPass("");
      setSelectedEmp(null);
      toast.success(signOutOthers ? "Password updated & other sessions logged out" : "Password updated successfully");
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || "Failed to change password");
    } finally {
      setPassLoading(false);
    }
  };

  const handleToggleLock = async (id, currentStatus) => {
    try {
      const { data } = await api.put(`/employees/lock/${id}`, { isLocked: !currentStatus });
      setEmployees((prev) => prev.map((e) => (e.id === id ? { ...e, isLocked: data.isLocked } : e)));
      toast.success(data.isLocked ? "Account Locked" : "Account Unlocked");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to toggle lock");
    }
  };

  const handleManagerChange = async (email, managerId) => {
    try {
      const { data } = await api.put(`/employees/manager/${encodeURIComponent(email)}`, { managerId: managerId || null });
      setEmployees((prev) => prev.map((e) => e.email === email ? { ...e, managerId: data.managerId, managerName: data.managerName } : e));
      toast.success("Manager assigned");
    } catch {
      toast.error("Failed to assign manager");
    }
  };

  const handleRoleChange = async (email, role) => {
    try {
      const { data } = await api.put(`/employees/role/${encodeURIComponent(email)}`, { role });
      setEmployees((prev) => prev.map((e) => e.email === email ? { ...e, role: data.role } : e));
      toast.success("Role updated");
    } catch {
      toast.error("Failed to update role");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to completely delete this employee? This cannot be undone.")) return;
    try {
      await api.delete(`/employees/${id}`);
      setEmployees((prev) => prev.filter((e) => (e.id || e._id) !== id));
      toast.success("Employee deleted");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to delete employee");
    }
  };

  return (
    <div className="cp-page">
      {/* ── Page Toolbar ── */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <span className="badge rounded-pill" style={{ background: 'var(--primary-light)', color: 'var(--primary)', border: '1px solid var(--primary-border)', padding: '6px 12px', fontSize: '12px', fontWeight: 600 }}>
            {employees.length} Staff Accounts
          </span>
          <span className="text-muted small d-none d-md-inline">System administration, employee accounts, roles, and brand management</span>
        </div>

        {isAdmin && (
          <div className="leads-actions">
            <button className="leads-btn primary" onClick={() => setShowModal(true)}>
              <UserPlus size={14} /> Add Employee
            </button>
          </div>
        )}
      </div>

      <Tabs defaultActiveKey="employees" className="mb-3">
        <Tab eventKey="employees" title="Employee Directory">
          <div className="tp-card">
            <div className="tp-card__head">
              <span className="tp-card__title">
                <Shield size={16} /> User Management
              </span>
            </div>

            {loading ? (
              <table className="tp-table">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Role</th><th>Manager</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4].map(n => (
                    <tr key={n}>
                      <td><div className="crm-skeleton-pulse" style={{ width: 120, height: 16 }} /></td>
                      <td><div className="crm-skeleton-pulse" style={{ width: 160, height: 14 }} /></td>
                      <td><div className="crm-skeleton-pulse" style={{ width: 70, height: 22, borderRadius: 12 }} /></td>
                      <td><div className="crm-skeleton-pulse" style={{ width: 100, height: 14 }} /></td>
                      <td><div className="crm-skeleton-pulse" style={{ width: 70, height: 24, borderRadius: 6, marginLeft: 'auto' }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="tp-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Manager</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => {
                    const empId = emp.id || emp._id;
                    return (
                      <tr key={empId}>
                        <td style={{ fontWeight: 600, color: 'var(--text)' }}>{emp.name}</td>
                        <td style={{ color: 'var(--muted)' }}>{emp.email}</td>
                        <td>
                          {isAdmin ? (
                            <select
                              value={emp.role}
                              onChange={(e) => handleRoleChange(emp.email, e.target.value)}
                              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: '#38bdf8', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}
                            >
                              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                          ) : (
                            <span className="cell-badge">{emp.role}</span>
                          )}
                        </td>
                        <td>
                          {isAdmin && emp.role === "Agent" ? (
                            <select
                              value={emp.managerId || ""}
                              onChange={(e) => handleManagerChange(emp.email, e.target.value)}
                              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}
                            >
                              <option value="">No Manager</option>
                              {managers.map((m) => <option key={m.id || m._id} value={m.id || m._id}>{m.name}</option>)}
                            </select>
                          ) : (
                            emp.managerName || "—"
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            {isAdmin && (
                              <button
                                className="quick-act-btn"
                                title="Change Password"
                                onClick={() => { setSelectedEmp(emp); setShowPassModal(true); }}
                              >
                                <Key size={12} />
                              </button>
                            )}
                            <button
                              className="quick-act-btn"
                              title={emp.isLocked ? "Unlock" : "Lock"}
                              onClick={() => handleToggleLock(empId, emp.isLocked)}
                            >
                              {emp.isLocked ? <Unlock size={12} /> : <Lock size={12} />}
                            </button>
                            {isAdmin && (
                              <button
                                className="quick-act-btn"
                                title="Delete Employee"
                                onClick={() => handleDelete(empId)}
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </Tab>

        <Tab eventKey="brands" title="Brand Settings">
          <BrandSettings />
        </Tab>

        <Tab eventKey="sessions" title="Security & Active Sessions">
          <ActiveSessions />
        </Tab>

        <Tab eventKey="compliance" title="Policy Compliance">
          <PolicyComplianceAdmin />
        </Tab>
      </Tabs>

      {/* Add Employee Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered dialogClassName="paj-clean-modal">
        <Modal.Header>
          <Modal.Title>Add Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group style={{ marginBottom: '16px' }}>
            <Form.Label>Name *</Form.Label>
            <Form.Control type="text" value={newEmp.name} onChange={(e) => setNewEmp(p => ({ ...p, name: e.target.value }))} />
          </Form.Group>
          <Form.Group style={{ marginBottom: '16px' }}>
            <Form.Label>Email *</Form.Label>
            <Form.Control type="email" value={newEmp.email} onChange={(e) => setNewEmp(p => ({ ...p, email: e.target.value }))} />
          </Form.Group>
          
          <PasswordInput
            label="Password"
            required
            name="newEmpPassword"
            value={newEmp.password}
            onChange={(e) => setNewEmp(p => ({ ...p, password: e.target.value }))}
            placeholder="Create a strong password"
            autoComplete="new-password"
          />
          {newEmp.password && (
            <PasswordStrengthMeter password={newEmp.password} />
          )}

          <Form.Group style={{ marginBottom: '16px', marginTop: '16px' }}>
            <Form.Label>Role</Form.Label>
            <Form.Select value={newEmp.role} onChange={(e) => setNewEmp(p => ({ ...p, role: e.target.value }))}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAdd} disabled={addEmpLoading}>
            {addEmpLoading ? "Adding..." : "Add Employee"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Change Password Modal */}
      <Modal show={showPassModal} onHide={() => { setShowPassModal(false); setSelectedEmp(null); }} centered className="dark-theme-modal">
        <Modal.Header style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', color: 'var(--text)' }}>
          <Modal.Title style={{ fontSize: '16px' }}>Change Password for {selectedEmp?.name || selectedEmp?.email}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: 'var(--surface)', color: 'var(--text)' }}>
          {(selectedEmp?.id === user?.id || selectedEmp?.email === user?.email) && (
            <PasswordInput
              label="Current Password"
              required
              name="currentPassword"
              value={currentPass}
              onChange={(e) => setCurrentPass(e.target.value)}
              placeholder="Enter current password"
              autoComplete="current-password"
            />
          )}

          <PasswordInput
            label="New Password"
            required
            name="newPassword"
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            placeholder="Enter new secure password (min 8 chars)"
            autoComplete="new-password"
          />

          {newPass && (
            <PasswordStrengthMeter password={newPass} confirmPassword={confirmPass} />
          )}

          <PasswordInput
            label="Confirm New Password"
            required
            name="confirmPassword"
            value={confirmPass}
            onChange={(e) => setConfirmPass(e.target.value)}
            placeholder="Re-enter new password"
            autoComplete="new-password"
            error={confirmPass && newPass !== confirmPass ? "Passwords do not match" : null}
          />

          <div style={{ marginTop: '12px' }}>
            <Form.Check
              type="checkbox"
              id="sign-out-others-check"
              label="Sign out of all other devices on password update"
              checked={signOutOthers}
              onChange={(e) => setSignOutOthers(e.target.checked)}
              style={{ fontSize: "12.5px", color: "var(--text)" }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
          <Button variant="secondary" onClick={() => { setShowPassModal(false); setSelectedEmp(null); }}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleChangePassword}
            disabled={passLoading || !newPass || newPass.length < 8 || newPass !== confirmPass}
          >
            {passLoading ? "Updating..." : "Update Password"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ControlPanel;